import {
    ActionType, DecisionTrackingType, MeetingInvitationPeriodType, MeetingMinutesContentType, MeetingSectionContentType, MeetingStatus,
    NetworkActionPrefix, RequestStatus
} from '../utils/constants';
import defaults from '../api/defaults.js';
import {
    combineReducers, networkActionReducer, networkEntityAddAllReducer, networkEntityAddReducer, networkEntityDeleteAllReducer, networkEntityDeleteReducer, networkEntityEditAllReducer,
    networkEntityEditReducer, networkEntityLoadReducer, networkObjectLoadReducer, networkObjectTrackChangesReducer, networkObjectVoidReducer, networkPaginationLoadReducer,
    paginationSlicePopReducer, paginationSlicePushReducer, pipeReducers, sortableEntitiesLifecycleReducer, synchronizableEnumReducer
} from '../utils/reducerFactory';
import { AgendaContentFilter } from 'typed/api';
import { deepAssign } from '../utils';
import { LOCATION_CHANGE } from 'react-router-redux';

function index(state, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

const keyVariables = {
    decisionIndex: (state, action) => index(state, action.decisionID ? action.decisionID : undefined),
    decisionTopicCommentIndex: (state, action) => index(state, action.decisionTopicCommentID || undefined),
    decisionTopicIndex: (state, action) => index(state, action.decisionTopicID),
    meetingIndex: (state, action) => index(state, action.meetingID || undefined),
    meetingSectionIndex: (state, action) => index(state, action.meetingSectionID || undefined),
    meetingSectionTopicIndex: (state, action) => index(state, action.meetingSectionTopicID || undefined),
    meetingSectionTopicAttachmentIndex: (state, action) => index(state, action.meetingSectionTopicAttachmentID || undefined),
    meetingSectionTopicCommentIndex: (state, action) => index(state, action.meetingSectionTopicCommentID || undefined),
    meetingTemplateIndex: (state, action) => index(state, action.meetingTemplateID || undefined),
    meetingTemplateSectionIndex: (state, action) => index(state, action.meetingTemplateSectionID || undefined),
    meetingTemplateSectionTopicIndex: (state, action) => index(state, action.meetingTemplateSectionTopicID || undefined),
    meetingMinutesIndex: (state, action) => index(state, action.meetingMinutesID || undefined)
};

export default pipeReducers(
    combineReducers({
        agendaFilter: (state, action) => {
            let nextState = state || {
                contentFilter: AgendaContentFilter.ME,
                companyFunctionID: null,
                virtualTeamID: null,
                targetUserID: null
            };

            if (action.type === ActionType.SET_AGENDA_FILTER) {
                let
                    contentFilter = action.contentFilter !== undefined ? action.contentFilter : nextState.contentFilter,
                    companyFunctionID = null,
                    virtualTeamID = null,
                    targetUserID = null;

                switch (true) {
                    case contentFilter === AgendaContentFilter.VIRTUAL_TEAM:
                        virtualTeamID = action.virtualTeamID || nextState.virtualTeamID || null;
                        if (!virtualTeamID) { contentFilter = AgendaContentFilter.ME; }

                        break;

                    case contentFilter === AgendaContentFilter.USER:
                        targetUserID = action.targetUserID || nextState.targetUserID || null;
                        if (!targetUserID) { contentFilter = AgendaContentFilter.ME; }

                        break;

                    case !contentFilter:
                        companyFunctionID = action.companyFunctionID || nextState.companyFunctionID || null;
                        if (!companyFunctionID) { contentFilter = AgendaContentFilter.ME; }

                        break;

                    default:
                        break;
                }

                nextState = deepAssign(nextState, {
                    contentFilter,
                    companyFunctionID,
                    virtualTeamID,
                    targetUserID
                });
            }

            return nextState;
        },
        agendaNavigationHistory: (state, action) => {
            const defaultState = state || { current: null, previous: null };
            if (action.type === LOCATION_CHANGE && /^\/agenda360\/\w+/.test(action.payload.pathname)) {
                return {
                    current: { pathname: action.payload.pathname, query: action.payload.query },
                    previous: defaultState.current
                };
            }
            else if (
                action.type === 'DROP_AGENDA_NAVIGATION_HISTORY' ||
                action.type === 'DELETE_COMPANY_FUNCTION' && action.payload.status === RequestStatus.SUCCEEDED &&
                defaultState.current && state.current.pathname.startsWith('/agenda360/insight_feed')
            ) {
                return { current: null, previous: null };
            }

            return defaultState;
        },
        calendarView: (state, action) => {
            const defaultState = state || null;

            if (action.type === LOCATION_CHANGE && (action.payload.pathname === '/agenda360/calendar' || action.payload.pathname === '/calendar/meetings')) {
                return +action.payload.query?.calendarView || defaultState;
            }

            return state;
        },
        decisions: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DECISIONS_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@decisionIndex': pipeReducers(
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_DECISION_PREFIX),
                        networkEntityAddReducer(NetworkActionPrefix.ADD_DECISION_PREFIX, null, (state, action) => action.decision),
                        networkEntityEditReducer(
                            NetworkActionPrefix.EDIT_DECISION_PREFIX, null, (state, action) => action.type === ActionType.EDIT_DECISION ? state : action.decision, true
                        ),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DECISION_PREFIX)
                    )
                }, keyVariables)
            }),
            (state, action) => {
                const defaultState = state || defaults.NetworkArray;

                if (action.type === ActionType.EDIT_MEETING_SUCCESS || action.type === ActionType.EDIT_ALL_MEETINGS_SUCCESS) {
                    const meetings = action.type === ActionType.EDIT_MEETING_SUCCESS ? [action.meeting] : action.meetings;

                    return deepAssign(defaultState, {
                        Data: defaultState.Data.map((decision) => {
                            const meeting = meetings.getByID(decision.MeetingID);
                            return meeting && Object.prototype.hasOwnProperty.call(meeting, 'FunctionID') ?
                                deepAssign(decision, { CompanyFunctionID: meeting.FunctionID }) :
                                decision;
                        })
                    });
                }

                if (action.type === 'DELETE_STRATEGIC_GOAL' && action.payload.status === RequestStatus.SUCCEEDED) {
                    return deepAssign(defaultState, {
                        Data: defaultState.Data.map(decision => deepAssign(decision, {
                            StrategicGoalID: decision.StrategicGoalID === action.payload.entityID ? null : decision.StrategicGoalID
                        }))
                    });
                }

                if (action.type === 'DELETE_STRATEGY_DESCRIPTION' && action.payload.status === RequestStatus.SUCCEEDED) {
                    return deepAssign(defaultState, { Custom: { status: RequestStatus.UNSYNCED } });
                }

                return defaultState;
            }
        ),
        decisionTrackingTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_DECISION_TRACKING_TYPES_PREFIX, defaults.NetworkArray, DecisionTrackingType, 'DecisionTrackingType'),
        decisionTopics: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DECISION_TOPICS_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@decisionTopicIndex': pipeReducers(
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_DECISION_TOPIC_PREFIX),
                        networkEntityAddReducer(NetworkActionPrefix.ADD_DECISION_TOPIC_PREFIX, null, (state, action) => action.decisionTopic),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DECISION_TOPIC_PREFIX),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_DECISION_TOPIC_PREFIX, null, (state, action) => action.decisionTopic),
                        networkActionReducer(NetworkActionPrefix.PUT_DECISION_TOPIC_BEFORE_ANOTHER_TOPIC_PREFIX),
                        networkActionReducer(NetworkActionPrefix.PUT_DECISION_TOPIC_AS_LAST_IN_PHASE_PREFIX),
                        combineReducers({
                            Comments: pipeReducers(
                                networkPaginationLoadReducer(NetworkActionPrefix.LOAD_DECISION_TOPIC_COMMENTS_SLICE_PREFIX),
                                combineReducers({
                                    Data: combineReducers({
                                        '@decisionTopicCommentIndex': networkEntityAddReducer(NetworkActionPrefix.ADD_DECISION_TOPIC_COMMENT_PREFIX, null, (state, action) => action.decisionTopicComment)
                                    }, keyVariables)
                                }, keyVariables)
                            )
                        })
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.PUT_DECISION_TOPIC_BEFORE_ANOTHER_TOPIC:
                    case ActionType.PUT_DECISION_TOPIC_AS_LAST_IN_PHASE:
                        return deepAssign(state, { Custom: { isSavingAfterDragAndDrop: true } });

                    case ActionType.LOAD_ALL_DECISION_TOPICS_SUCCESS:
                        return deepAssign(state, { Custom: { isSavingAfterDragAndDrop: false } });

                    case ActionType.EDIT_MEETING_STATUS_SUCCESS:
                        return action.statusID === MeetingStatus.ARCHIVED ?
                            deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } }) :
                            state;

                    case ActionType.VOID_ALL_STRATEGY_DESCRIPTIONS:
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });

                    default:
                        return state;
                }
            }
        ),
        decisionTopicSearchSuggestions: networkObjectLoadReducer(NetworkActionPrefix.LOAD_DECISION_TOPIC_SUGGESTIONS_PREFIX, defaults.NetworkArray),
        decisionTopicTypes: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DECISION_TOPIC_TYPES_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: pipeReducers(
                    networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DECISION_TOPIC_TYPES_PREFIX, null, (state, action) => action.decisionTopicTypes),
                    networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DECISION_TOPIC_TYPES_PREFIX, null, (state, action) => action.decisionTopicTypeIDs),
                    networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DECISION_TOPIC_TYPES_PREFIX, null, (state, action) => action.decisionTopicTypes)
                )
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.ADD_ALL_DECISION_TOPIC_TYPES_SUCCESS:
                    case ActionType.EDIT_ALL_DECISION_TOPIC_TYPES_SUCCESS:
                        return deepAssign(state, { Data: state.Data.copy().sortBy(type => type.Name) });

                    default:
                        return state;
                }
            }
        ),
        meetingInvitationPeriodTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_MEETING_INVITATION_PERIOD_TYPES_PREFIX, defaults.NetworkArray, MeetingInvitationPeriodType, 'MeetingInvitationPeriodType'),
        meetings: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_MEETINGS_PREFIX, defaults.NetworkArray),
            (state, action) => {
                let nextState = state;

                if (action.type === ActionType.LOAD_MANY_MEETINGS_PENDING || action.type === ActionType.LOAD_MANY_MEETINGS_FAIL) {
                    for (let index = 0; index < action.meetingIDs.length; index++) {
                        const meetingID = action.meetingIDs[index];

                        nextState = deepAssign(nextState, {
                            Data: {
                                [nextState.Data.getIndexByID(meetingID)]: {
                                    Custom: {
                                        status: action.type === ActionType.LOAD_MANY_MEETINGS_PENDING ? RequestStatus.PENDING : RequestStatus.FAILED
                                    }
                                }
                            }
                        });
                    }
                }

                if (action.type === ActionType.LOAD_MANY_MEETINGS_SUCCESS) {
                    for (let index = 0; index < action.meetingIDs.length; index++) {
                        const
                            meetingID = action.meetingIDs[index],
                            response = action.response[index];

                        let meetingIndex = nextState.Data.getIndexByID(meetingID);
                        if (meetingIndex < 0) { meetingIndex = nextState.Data.length; }

                        nextState = deepAssign(nextState, {
                            Data: {
                                [meetingIndex]: response
                            }
                        });
                    }
                }

                return nextState;
            },
            combineReducers({
                Data: combineReducers({
                    '@meetingIndex': pipeReducers(
                        networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_PREFIX, null, (state, action) => action.meeting),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_PREFIX),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_PREFIX, null, (state, action) => action.meeting),
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_MEETING_PREFIX),
                        networkEntityLoadReducer(NetworkActionPrefix.RECREATE_MEETING_CONTENT_PREFIX),
                        networkEntityLoadReducer(NetworkActionPrefix.ADD_CONTENT_TO_MEETING_PREFIX),
                        networkActionReducer(NetworkActionPrefix.UPLOAD_MEETING_MINUTES_PREFIX),
                        networkActionReducer(NetworkActionPrefix.INVITE_MEETING_MINUTES_EDITOR_PREFIX),
                        networkActionReducer(NetworkActionPrefix.GENERATE_MEETING_MINUTES_PREFIX),
                        networkActionReducer(NetworkActionPrefix.GENERATE_MEETING_MINUTES_PDF_PREFIX),
                        networkActionReducer(NetworkActionPrefix.GENERATE_MEETING_MINUTES_WORD_DOC_PREFIX),
                        networkActionReducer(NetworkActionPrefix.SIGN_MEETING_MINUTES_PREFIX),
                        networkActionReducer(NetworkActionPrefix.DOWNLOAD_MEETING_PDF_PREFIX),
                        networkActionReducer(NetworkActionPrefix.DELETE_MEETING_MINUTES_FILE_PREFIX),
                        networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_MEETINGS_PREFIX, null, (state, action) => action.meetings),
                        combineReducers({
                            Custom: combineReducers({
                                invitationStatus: networkActionReducer(NetworkActionPrefix.SEND_MEETING_INVITATIONS_PREFIX)
                            }),
                            Administrators: pipeReducers(
                                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_MEETING_ADMINISTRATORS_PREFIX, null, (state, action) => action.meetingAdministrators),
                                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_MEETING_ADMINISTRATORS_PREFIX, null, (state, action) => action.meetingAdministratorIDs)
                            ),
                            Participants: pipeReducers(
                                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_MEETING_PARTICIPANTS_PREFIX, null, (state, action) => action.meetingParticipants),
                                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_MEETING_PARTICIPANTS_PREFIX, null, (state, action) => action.meetingParticipantIDs),
                                (state, action) => {
                                    if (action.type === ActionType.TOUCH_MEETING_PARTICIPANT_PENDING) {
                                        return deepAssign(state, {
                                            [state.getIndexByID(action.meetingParticipantID)]: {
                                                Custom: { status: RequestStatus.PENDING }
                                            }
                                        });
                                    }

                                    if (action.type === ActionType.TOUCH_MEETING_PARTICIPANT_SUCCESS) {
                                        return deepAssign(state, {
                                            [state.getIndexByID(action.meetingParticipantID)]: {
                                                MeetingLastSeen: new Date(),
                                                Custom: { status: RequestStatus.SUCCEEDED }
                                            }
                                        });
                                    }

                                    return state;
                                }
                            ),
                            Sections: pipeReducers(
                                networkPaginationLoadReducer(NetworkActionPrefix.LOAD_ALL_MEETING_SECTIONS_PREFIX, defaults.NetworkPaginationSliceOf(defaults.MeetingSectionTopicReport)),
                                paginationSlicePushReducer(ActionType.ADD_MEETING_SECTION_TOPIC_SUCCESS, defaults.NetworkPaginationSliceOf(defaults.MeetingSectionTopicReport)),
                                paginationSlicePopReducer(ActionType.DELETE_MEETING_SECTION_TOPIC_SUCCESS, defaults.NetworkPaginationSliceOf(defaults.MeetingSectionTopicReport)),
                                combineReducers({
                                    Data: sortableEntitiesLifecycleReducer('MeetingID', {
                                        '@meetingSectionIndex': pipeReducers(
                                            networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_SECTION_PREFIX, null, (state, action) => action.meetingSection),
                                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_SECTION_PREFIX, null),
                                            networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_SECTION_PREFIX, null, (state, action) => action.meetingSection),
                                            combineReducers({
                                                SectionTopics: sortableEntitiesLifecycleReducer('SectionID', {
                                                    '@meetingSectionTopicIndex': pipeReducers(
                                                        networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_SECTION_TOPIC_PREFIX, null, (state, action) => action.meetingSectionTopic),
                                                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_SECTION_TOPIC_PREFIX),
                                                        networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_SECTION_TOPIC_PREFIX, null, (state, action) => action.meetingSectionTopic),
                                                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_MEETING_SECTION_TOPIC_PREFIX),
                                                        networkEntityLoadReducer(NetworkActionPrefix.UNLINK_MEETING_SECTION_TOPIC_CONTENT_PREFIX),
                                                        combineReducers({
                                                            Attachments: combineReducers({
                                                                '@meetingSectionTopicAttachmentIndex': pipeReducers(
                                                                    networkActionReducer(NetworkActionPrefix.DOWNLOAD_MEETING_SECTION_TOPIC_ATTACHMENT_PREFIX),
                                                                    networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_SECTION_TOPIC_ATTACHMENT_PREFIX, null, (state, action) => action.meetingSectionTopicAttachment),
                                                                    networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_SECTION_TOPIC_ATTACHMENT_PREFIX, null, (state, action) => ({ ...action.meetingSectionTopicAttachment, BlobStorageURI: state.BlobStorageURI })),
                                                                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_SECTION_TOPIC_ATTACHMENT_PREFIX),
                                                                    networkEntityLoadReducer(NetworkActionPrefix.LOAD_MEETING_SECTION_TOPIC_ATTACHMENT_PREFIX)
                                                                )
                                                            }, keyVariables),
                                                            Comments: pipeReducers(
                                                                networkPaginationLoadReducer(NetworkActionPrefix.LOAD_MEETING_SECTION_TOPIC_COMMENTS_SLICE_PREFIX),
                                                                paginationSlicePushReducer(ActionType.ADD_MEETING_SECTION_TOPIC_COMMENT_SUCCESS, []),
                                                                combineReducers({
                                                                    Data: combineReducers({
                                                                        '@meetingSectionTopicCommentIndex': pipeReducers(
                                                                            networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_SECTION_TOPIC_COMMENT_PREFIX, null, (state, action) => action.meetingSectionTopicComment),
                                                                            networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_SECTION_TOPIC_COMMENT_PREFIX, null, (state, action) => action.meetingSectionTopicComment)
                                                                        )
                                                                    }, keyVariables)
                                                                }, keyVariables)
                                                            ),
                                                            TextTopic: networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_SECTION_TEXT_TOPIC_PREFIX, null, (state, action) => action.meetingSectionTextTopic),
                                                            TopicLink: networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_SECTION_TOPIC_LINK_PREFIX, null, (state, action) => action.meetingSectionTopicLink)
                                                        }, keyVariables)
                                                    )
                                                }, keyVariables)
                                            })
                                        )
                                    }, keyVariables)
                                }, keyVariables)
                            )
                        }),
                        (state, action) => {
                            if (!state) {
                                return state;
                            }

                            switch (action.type) {
                                case ActionType.GENERATE_MEETING_MINUTES_WORD_DOC_PENDING:
                                    return deepAssign(state, { Custom: { isGeneratingMeetingMinutes: true } });

                                case ActionType.GENERATE_MEETING_MINUTES_WORD_DOC_SUCCESS:
                                    return deepAssign(state, { Custom: { isGeneratingMeetingMinutes: false } });

                                case ActionType.GENERATE_MEETING_MINUTES_PDF_PENDING:
                                    return deepAssign(state, { GenerateArchiveStartDate: new Date() });

                                case ActionType.GENERATE_MEETING_MINUTES_PDF_SUCCESS:
                                    return deepAssign(state, { BlobStorageURI: action.response, GenerateArchiveStartDate: null });

                                case ActionType.SEND_ARCHIVED_MEETING_PDF_PENDING:
                                    return deepAssign(state, { Custom: { isSendingMeetingMinutes: true } });

                                case ActionType.SEND_ARCHIVED_MEETING_PDF_SUCCESS:
                                    return deepAssign(state, { Custom: { isSendingMeetingMinutes: false } });

                                case ActionType.UPLOAD_MEETING_MINUTES_SUCCESS:
                                    return deepAssign(state, { CustomMinutesBlobStorageURI: action.response });

                                case ActionType.GENERATE_MEETING_MINUTES_SUCCESS:
                                    return deepAssign(state, { GenerateArchiveStartDate: new Date() });

                                case ActionType.SIGN_MEETING_MINUTES_SUCCESS:
                                    return deepAssign(state, { StatusID: MeetingStatus.SIGNING });

                                case ActionType.DELETE_MEETING_MINUTES_FILE_SUCCESS:
                                    return deepAssign(state, { CustomMinutesBlobStorageURI: '', BlobStorageURI: '' });

                                case ActionType.EDIT_MEETING_SECTION_TOPIC_SUCCESS:
                                    return deepAssign(state, {
                                        Sections: {
                                            Data: state.Sections.Data.map(section => section.ID !== action.meetingSectionID ?
                                                { ...section, SectionTopics: section.SectionTopics.filter(({ ID }) => ID !== action.meetingSectionTopicID) } :
                                                section
                                            )
                                        }
                                    });

                                case ActionType.EDIT_MEETING_STATUS_SUCCESS:
                                    let meetingState = deepAssign(state, { StatusID: action.statusID });

                                    if (action.statusID !== MeetingStatus.ARCHIVED && state.StatusID === MeetingStatus.ARCHIVED) {
                                        meetingState = deepAssign(meetingState, {
                                            BlobStorageURI: '',
                                            Sections: {
                                                Data: meetingState.Sections.Data.map(section => ({
                                                    ...section,
                                                    SectionTopics: section.SectionTopics.map(topic => ({ ...topic, BlobStorageURI: '' }))
                                                }))
                                            }
                                        });
                                    }

                                    return meetingState;

                                case ActionType.SAVE_MEETING_REPORT_AS_TEMPLATE_SUCCESS:
                                    if (action.deleteSourceMeeting) {
                                        return null;
                                    }
                                    else {
                                        return deepAssign(state, { TemplateID: action.response });
                                    }

                                case ActionType.SEND_MEETING_INVITATIONS_SUCCESS:
                                    return deepAssign(state, {
                                        InvitesSent: true,
                                        InvitesSendDate: new Date(),
                                        Participants: state.Participants.map(participant => action.invitedUsers.includes(participant.UserID) ?
                                            { ...participant, InviteSent: new Date() } :
                                            participant)
                                    });

                                default:
                                    return state;
                            }
                        }
                    )
                }, keyVariables)
            }),
            networkObjectVoidReducer(ActionType.VOID_ALL_MEETINGS, defaults.NetworkArray),
            (state, action) => {
                if (action.type === 'CONTERT_AGENDA_TIME_SERIE_EVENT_TO_ENTITY') {
                    return deepAssign(state, { Custom: { expires: new Date() } });
                }

                if (action.type === ActionType.CLONE_MEETING_SUCCESS) {
                    return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });
                }

                if (action.type === ActionType.DELETE_MEETING_TEMPLATE_SUCCESS) {
                    return deepAssign(state, {
                        Data: state.Data.map(item =>
                            item.TemplateID === action.meetingTemplateID ? { ...item, TemplateID: defaults.MeetingReport.TemplateID } : item)
                    });
                }

                if (action.type === ActionType.MOVE_MEETING_SECTION_TOPIC_TO_ANOTHER_MEETING_SUCCESS) {
                    return deepAssign(state, {
                        Data: state.Data.map(item =>
                            item.ID === action.sourceMeetingID ? {
                                ...item,
                                Sections: {
                                    Data: item.Sections.Data.map(section => ({
                                        ...section,
                                        SectionTopics: section.SectionTopics.filter(({ ID }) => ID !== action.meetingSectionTopic.ID)
                                    }))
                                }
                            } : item)
                    });
                }

                if (action.type === ActionType.EDIT_ALL_MEETINGS_SUCCESS) {
                    return deepAssign(state, {
                        Data: state.Data.map((item) => {
                            const editedItem = action.meetings.getByID(item.ID);
                            return editedItem ? { ...item, ...editedItem } : item;
                        })
                    });
                }

                return state;
            }
        ),
        meetingSectionContentTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_MEETING_SECTION_CONTENT_TYPES_PREFIX, defaults.NetworkArray, MeetingSectionContentType, 'MeetingSectionContentType'),
        meetingStatuses: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_MEETING_STATUSES_PREFIX, defaults.NetworkArray, MeetingStatus, 'MeetingStatus'),
        meetingTemplates: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_MEETING_TEMPLATES_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: combineReducers({
                    '@meetingTemplateIndex': pipeReducers(
                        networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_TEMPLATE_PREFIX, null, (state, action) => action.meetingTemplate),
                        networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_TEMPLATE_PREFIX, null, (state, action) => action.meetingTemplate),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_TEMPLATE_PREFIX),
                        combineReducers({
                            Administrators: pipeReducers(
                                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_MEETING_TEMPLATE_ADMINISTRATORS_PREFIX, null, (state, action) => action.meetingTemplateAdministrators),
                                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_MEETING_TEMPLATE_ADMINISTRATORS_PREFIX, null, (state, action) => action.meetingTemplateAdministratorIDs)
                            ),
                            Participants: pipeReducers(
                                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_MEETING_TEMPLATE_PARTICIPANTS_PREFIX, null, (state, action) => action.meetingTemplateParticipants),
                                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_MEETING_TEMPLATE_PARTICIPANTS_PREFIX, null, (state, action) => action.meetingTemplateParticipantIDs)
                            ),
                            Sections: sortableEntitiesLifecycleReducer('TemplateID', {
                                '@meetingTemplateSectionIndex': pipeReducers(
                                    networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_TEMPLATE_SECTION_PREFIX, null, (state, action) => action.meetingTemplateSection),
                                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_TEMPLATE_SECTION_PREFIX, null),
                                    networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_TEMPLATE_SECTION_PREFIX, null, (state, action) => action.meetingTemplateSection),
                                    combineReducers({
                                        SectionTopics: sortableEntitiesLifecycleReducer('SectionID', {
                                            '@meetingTemplateSectionTopicIndex': pipeReducers(
                                                networkEntityAddReducer(NetworkActionPrefix.ADD_MEETING_TEMPLATE_SECTION_TOPIC_PREFIX, null, (state, action) => action.meetingTemplateSectionTopic),
                                                networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_TEMPLATE_SECTION_TOPIC_PREFIX),
                                                networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_TEMPLATE_SECTION_TOPIC_PREFIX, null, (state, action) => action.meetingTemplateSectionTopic),
                                                networkEntityLoadReducer(NetworkActionPrefix.LOAD_MEETING_TEMPLATE_SECTION_TOPIC_PREFIX),
                                                combineReducers({
                                                    TextTopic: networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_TEMPLATE_SECTION_TEXT_TOPIC_PREFIX, null, (state, action) => action.meetingTemplateSectionTextTopic),
                                                    TopicLink: networkEntityEditReducer(NetworkActionPrefix.EDIT_MEETING_TEMPLATE_SECTION_TOPIC_LINK_PREFIX, null, (state, action) => action.meetingTemplateSectionTopicLink)
                                                })
                                            )
                                        }, keyVariables)
                                    })
                                )
                            }, keyVariables)
                        })
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.SAVE_MEETING_REPORT_AS_TEMPLATE_SUCCESS:
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });

                    default:
                        return state;
                }
            }
        ),
        meetingMinutes: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_MEETING_MINUTESES_PREFIX, defaults.NetworkArray),
            networkObjectTrackChangesReducer(['MeetingMinutesReport']),
            combineReducers({
                Data: combineReducers({
                    '@meetingMinutesIndex': pipeReducers(
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_MEETING_MINUTES_PREFIX),
                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_MEETING_MINUTES_PREFIX)
                    )
                }, keyVariables)
            }),
            (state, action) => {
                switch (action.type) {
                    case ActionType.ADD_ENTITY_CHANGE:
                        if (action.change.EntityType === defaults.MeetingMinutesReport.EntityType) {
                            return deepAssign(state, {
                                Data: state.Data.map(x => x.ID === action.change.EntityID ? { ...x, Custom: { status: RequestStatus.UNSYNCED } } : x)
                            });
                        }

                        return state;

                    default:
                        return state;
                }
            }
        ),
        meetingMinutesContentTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_MEETING_MINUTES_CONTENT_TYPES_PREFIX, defaults.NetworkArray, MeetingMinutesContentType, 'MeetingMinutesContentType'),
        phases: pipeReducers(
            networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DECISION_TOPIC_PHASES_PREFIX, defaults.NetworkArray),
            combineReducers({
                Data: sortableEntitiesLifecycleReducer('', pipeReducers(
                    networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DECISION_TOPIC_PHASES_PREFIX, null, (state, action) => action.decisionTopicPhases),
                    networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DECISION_TOPIC_PHASES_PREFIX, null, (state, action) => action.decisionTopicPhaseIDs),
                    networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DECISION_TOPIC_PHASES_PREFIX, null, (state, action) => action.decisionTopicPhases)
                ), keyVariables)
            }),
            (state, action) => {
                if (action.type === ActionType.ADD_DECISION_TOPIC_SUCCESS && !action.decisionTopic.PhaseID) {
                    return defaults.NetworkArray;
                }

                return state;
            }

        )
    }),
    (state, action) => {
        switch (action.type) {
            case ActionType.ADD_MEETING_SUCCESS:
                return deepAssign(state, {
                    decisionTopics: {
                        Custom: { status: RequestStatus.UNSYNCED }
                    },
                    meetings: {
                        Custom: { status: RequestStatus.UNSYNCED }
                    }
                });

            default:
                return state;
        }
    }
);
